/**
 * Service centralisé de gestion des prix
 *
 * RÈGLE IMPORTANTE: Tous les prix sont stockés en CENTIMES dans la base de données
 * Exemple: 839€ est stocké comme 83900 centimes
 *
 * Ce service assure la cohérence du formatage des prix partout dans l'application
 */

import { logger } from '@/lib/logger';

export interface PriceData {
  priceInCents: number;
  currency?: string;
}

export interface FormattedPrice {
  displayValue: string;      // "839,00 €"
  numericValue: number;       // 839 (en euros)
  centsValue: number;         // 83900 (en centimes)
  currency: string;           // "EUR"
}

/**
 * Formate un prix stocké en centimes pour l'affichage
 * @param priceInCents - Prix en centimes (ex: 83900)
 * @param currency - Devise (par défaut: 'EUR')
 * @param locale - Locale pour le formatage (par défaut: 'fr-FR')
 * @returns Prix formaté avec symbole (ex: "839,00 €")
 */
export function formatPrice(
  priceInCents: number | null | undefined,
  currency: string = 'EUR',
  locale: string = 'fr-FR'
): string {
  // Gérer les valeurs nulles/undefined
  if (priceInCents === null || priceInCents === undefined || isNaN(priceInCents)) {
    return '0,00 €';
  }

  // Si le prix est 0, retourner "Inclus" ou "Gratuit"
  if (priceInCents === 0) {
    return 'Inclus';
  }

  // Convertir centimes → euros et formater
  const priceInEuros = priceInCents / 100;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(priceInEuros);
}

/**
 * Formate un prix avec des détails complets
 * @param priceInCents - Prix en centimes
 * @param currency - Devise
 * @returns Objet avec plusieurs formats du prix
 */
export function formatPriceDetailed(
  priceInCents: number | null | undefined,
  currency: string = 'EUR'
): FormattedPrice {
  const safeCents = priceInCents ?? 0;
  const euros = safeCents / 100;

  return {
    displayValue: formatPrice(safeCents, currency),
    numericValue: euros,
    centsValue: safeCents,
    currency: currency.toUpperCase()
  };
}

/**
 * Convertit un montant en euros vers centimes
 * @param priceInEuros - Prix en euros (ex: 839)
 * @returns Prix en centimes (ex: 83900)
 */
export function eurosToCents(priceInEuros: number): number {
  return Math.round(priceInEuros * 100);
}

/**
 * Convertit un montant en centimes vers euros
 * @param priceInCents - Prix en centimes (ex: 83900)
 * @returns Prix en euros (ex: 839)
 */
export function centsToEuros(priceInCents: number): number {
  return priceInCents / 100;
}

/**
 * Calcule le prix TTC à partir du prix HT
 * @param priceHTInCents - Prix HT en centimes
 * @param vatRate - Taux de TVA en pourcentage (ex: 20 pour 20%)
 * @returns Prix TTC en centimes
 */
export function calculateTTC(priceHTInCents: number, vatRate: number = 20): number {
  return Math.round(priceHTInCents * (1 + vatRate / 100));
}

/**
 * Calcule le prix HT à partir du prix TTC
 * @param priceTTCInCents - Prix TTC en centimes
 * @param vatRate - Taux de TVA en pourcentage (ex: 20 pour 20%)
 * @returns Prix HT en centimes
 */
export function calculateHT(priceTTCInCents: number, vatRate: number = 20): number {
  return Math.round(priceTTCInCents / (1 + vatRate / 100));
}

/**
 * Calcule le montant de la TVA
 * @param priceHTInCents - Prix HT en centimes
 * @param vatRate - Taux de TVA en pourcentage
 * @returns Montant de la TVA en centimes
 */
export function calculateVAT(priceHTInCents: number, vatRate: number = 20): number {
  return Math.round(priceHTInCents * (vatRate / 100));
}

/**
 * Interface pour les promotions
 */
export interface Promotion {
  discount_percentage?: number;
  discount_amount?: number;  // En CENTIMES
  is_active?: boolean;
  start_date?: string;
  end_date?: string;
}

/**
 * Interface pour les informations de prix avec promotion
 */
export interface PricingInfo {
  originalPrice: number;      // En centimes
  discountedPrice: number;    // En centimes
  discountPercentage: number; // Pourcentage de réduction
  hasDiscount: boolean;       // Y a-t-il une réduction active
  savings: number;            // Économie en centimes
}

/**
 * Calcule le prix avec promotion appliquée
 * @param priceInCents - Prix original en centimes
 * @param promotions - Liste des promotions
 * @returns Informations complètes sur le prix avec promotion
 */
export function computeDiscountedPrice(
  priceInCents: number,
  promotions: Promotion[] = []
): PricingInfo {
  let discountedPrice = priceInCents;
  let maxDiscountPercentage = 0;

  // Filtrer les promotions actives
  const now = new Date();
  const activePromotions = promotions.filter(promo => {
    if (!promo.is_active) return false;

    const startDate = promo.start_date ? new Date(promo.start_date) : null;
    const endDate = promo.end_date ? new Date(promo.end_date) : null;

    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;

    return true;
  });

  // Appliquer la meilleure réduction
  activePromotions.forEach(promo => {
    // Réduction en pourcentage
    if (promo.discount_percentage) {
      const discountAmount = Math.round(priceInCents * (promo.discount_percentage / 100));
      const newPrice = priceInCents - discountAmount;

      if (newPrice < discountedPrice) {
        discountedPrice = newPrice;
        maxDiscountPercentage = promo.discount_percentage;
      }
    }

    // Réduction en montant fixe (déjà en centimes)
    if (promo.discount_amount) {
      const newPrice = priceInCents - promo.discount_amount;

      if (newPrice < discountedPrice) {
        discountedPrice = newPrice;
        // Calculer le pourcentage équivalent
        maxDiscountPercentage = Math.round((promo.discount_amount / priceInCents) * 100);
      }
    }
  });

  // S'assurer que le prix ne devient jamais négatif
  discountedPrice = Math.max(0, discountedPrice);

  return {
    originalPrice: priceInCents,
    discountedPrice,
    discountPercentage: maxDiscountPercentage,
    hasDiscount: discountedPrice < priceInCents,
    savings: priceInCents - discountedPrice
  };
}

/**
 * Formate un objet prix complet pour l'affichage
 * @param pricingInfo - Informations de prix
 * @param currency - Devise
 * @returns Objet avec les prix formatés
 */
export function formatPricingInfo(
  pricingInfo: PricingInfo,
  currency: string = 'EUR'
) {
  return {
    original: formatPrice(pricingInfo.originalPrice, currency),
    discounted: formatPrice(pricingInfo.discountedPrice, currency),
    savings: formatPrice(pricingInfo.savings, currency),
    discountPercentage: `${pricingInfo.discountPercentage}%`,
    hasDiscount: pricingInfo.hasDiscount
  };
}

/**
 * Valide qu'un prix est bien en centimes (> 1000 pour la plupart des formations)
 * Utile pour détecter les erreurs de conversion
 * @param price - Prix à valider
 * @param minPriceInCents - Prix minimum attendu en centimes (par défaut 1000 = 10€)
 * @returns true si le prix semble être en centimes, false sinon
 */
export function validatePriceFormat(price: number, minPriceInCents: number = 1000): boolean {
  // Un prix de formation devrait généralement être > 10€ (1000 centimes)
  // Si un prix est < 1000, il est probablement en euros et non en centimes
  return price >= minPriceInCents;
}

/**
 * Détecte et corrige automatiquement un prix qui serait en euros au lieu de centimes
 * @param price - Prix à vérifier
 * @param threshold - Seuil de détection (par défaut 10000 = 100€)
 * @returns Prix en centimes
 */
export function ensurePriceInCents(price: number, threshold: number = 10000): number {
  // Si le prix est très petit (< 100€), il est probablement déjà en euros
  if (price < threshold) {
    logger.warn('Prix suspect détecté, conversion euros → centimes appliquée', { price, threshold });
    return eurosToCents(price);
  }
  return price;
}

// Export de l'ancien nom pour rétrocompatibilité
export const formatPriceFromCents = formatPrice;
